import React, { useState } from "react";
import { Grid, TextField, Alert, CircularProgress, Button, Box, InputAdornment, MenuItem } from "@mui/material";
import FormContainer from "../../../../components/Forms/FormContainer";
import { ExecutePostWithParams } from "../../../../services/api/ExecuteApiRequests";
import { IPDCAttachFile1 } from "../../../../components/Controls/IPDCAttachFile";

// prettier-ignore
const initialData = { tin_no: "", company_name: "", contact_person: "", contact_person_position: "", phone_no: "",
                       mobile_no: "", email: "", website: "", nationality_origin: "", capital_in_usd: 0, description: "" };

// Validation helper functions
const validateEmail = (email) => {
  if (!email) return false; // Required field
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneNumber = (phone) => {
  if (!phone) return false; // Required field
  const phoneRegex = /^(\+?251|0)?[1-9]\d{8}$/;
  return phoneRegex.test(phone);
};

const validateMobileNumber = (mobile) => {
  if (!mobile) return false; // Required field
  const mobileRegex = /^(\+?251|0)?9[0-9]{8}$/;
  return mobileRegex.test(mobile);
};

const validateTIN = (tin) => {
  if (!tin) return true; // NOT required - skip validation if empty
  return tin.length >= 10 && tin.length <= 13;
};

const validateRequired = (value) => {
  if (typeof value === 'number') return value > 0;
  return value && value.toString().trim() !== "";
};

// Get error message for fields
const getFieldError = (name, value) => {
  switch (name) {
    case "tin_no":
      if (value && !validateTIN(value)) return "TIN number must be 10-13 characters";
      return "";
    case "company_name":
      if (!validateRequired(value)) return "Company name is required";
      return "";
    case "contact_person":
      if (!validateRequired(value)) return "Contact person name is required";
      return "";
    case "contact_person_position":
      // NOT required - skip validation
      return "";
    case "email":
      if (!validateRequired(value)) return "Email address is required";
      if (!validateEmail(value)) return "Invalid email format. Example: name@domain.com";
      return "";
    case "phone_no":
      if (!validateRequired(value)) return "Phone number is required";
      if (!validatePhoneNumber(value)) return "Invalid phone number. Examples: 0912345678, 0112345678, +251912345678";
      return "";
    case "mobile_no":
      if (!validateRequired(value)) return "Mobile number is required";
      if (!validateMobileNumber(value)) return "Invalid mobile number. Examples: 0912345678, +251912345678";
      return "";
    case "nationality_origin":
      if (!validateRequired(value)) return "Nationality origin is required";
      return "";
    case "capital_in_usd":
      if (!validateRequired(value)) return "Capital amount is required";
      if (value <= 0) return "Capital must be greater than 0";
      return "";
    default:
      return "";
  }
};

// List of all countries
const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad",
  "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador",
  "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
  "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
  "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania",
  "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique",
  "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
  "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
  "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago",
  "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

export default function RegisterNewInvestor() {
  const [formData, setFormData] = useState(initialData);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [isOtherCountry, setIsOtherCountry] = useState(false);
  const [otherCountryValue, setOtherCountryValue] = useState("");

  const handleChange = async event => {
    const { name, value } = event.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = getFieldError(name, value);
    if (error) {
      setFormErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateAllFields = () => {
    const errors = {};
    let isValid = true;
    
    // Validate required fields (excluding tin_no and contact_person_position)
    const fieldsToValidate = [
      "company_name", "contact_person", "email", "phone_no", "mobile_no", "nationality_origin", "capital_in_usd"
    ];
    
    fieldsToValidate.forEach(field => {
      const value = formData[field];
      const error = getFieldError(field, value);
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    });
    
    // Validate TIN only if it has a value (not required)
    if (formData.tin_no && !validateTIN(formData.tin_no)) {
      errors.tin_no = "TIN number must be 10-13 characters";
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  const handleCountryChange = async event => {
    const value = event.target.value;
    if (value === "other") {
      setIsOtherCountry(true);
      setFormData(prevData => ({ ...prevData, nationality_origin: "" }));
      setFormErrors(prev => ({ ...prev, nationality_origin: "" }));
    } else {
      setIsOtherCountry(false);
      setFormData(prevData => ({ ...prevData, nationality_origin: value }));
      if (value) {
        setFormErrors(prev => ({ ...prev, nationality_origin: "" }));
      }
    }
    setTouched(prev => ({ ...prev, nationality_origin: true }));
  };

  const handleOtherCountryChange = async event => {
    const value = event.target.value;
    setOtherCountryValue(value);
    setFormData(prevData => ({ ...prevData, nationality_origin: value }));
    if (value) {
      setFormErrors(prev => ({ ...prev, nationality_origin: "" }));
    }
  };

  const handleOtherCountryBlur = () => {
    if (!otherCountryValue) {
      setFormErrors(prev => ({ ...prev, nationality_origin: "Country name is required" }));
    }
  };

  const handleCurrencyChange = async event => {
    setCurrency(event.target.value);
  };

  const handleAttachmentsChange = files => {
    setAttachedFiles(files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  // const handleSubmit = async event => {
  //   console.log("Submit started");
  //   event.preventDefault();
    
  //   // Mark all fields as touched to show errors
  //   const allFields = ["company_name", "contact_person", "email", "phone_no", "mobile_no", "nationality_origin", "capital_in_usd"];
  //   const newTouched = {};
  //   allFields.forEach(field => { newTouched[field] = true; });
  //   setTouched(newTouched);
    
  //   // Validate all fields before submission
  //   if (!validateAllFields()) {
  //     setImportError("Please fix the validation errors before submitting.");
  //     window.scrollTo({ top: 0, behavior: 'smooth' });
  //     return;
  //   }
    
  //   setIsLoading(true);
  //   setImportError(null);
    
  //   const formDataWithFiles = new FormData();
  //   try {
  //     Object.entries(formData).forEach(([key, value]) => {
  //       if (value !== undefined && value !== null && value !== "") {
  //         formDataWithFiles.append(key, value);
  //       }
  //     });

  //     if (selectedFile) {
  //       formDataWithFiles.append("file", selectedFile);
  //     }
  //     formDataWithFiles.append("currency", currency);

  //     const responseData = await ExecutePostWithParams("investors", formDataWithFiles, true);
  //     console.log(responseData);
  //     if (responseData) {
  //       setImportSuccess(responseData.message || "Investor registered successfully!");
  //       setFormData(initialData);
  //       setFormErrors({});
  //       setTouched({});
  //       setAttachedFiles([]);
  //       setSelectedFile(null);
  //       setCurrency("USD");
  //       setIsOtherCountry(false);
  //       setOtherCountryValue("");
  //       setTimeout(() => setImportSuccess(null), 5000);
  //     } else {
  //       setImportError("Failed to save data");
  //     }
  //   } catch (error) {
  //     setImportError(error.response?.data?.message || "An error occurred during registration. Please try again.");
  //     console.log("Failed to save data", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
//*********************************************************** new updated code */
const handleSubmit = async event => {
  console.log("Submit started");
  event.preventDefault();
  
  // Mark all fields as touched to show errors
  const allFields = ["company_name", "contact_person", "email", "phone_no", "mobile_no", "nationality_origin", "capital_in_usd"];
  const newTouched = {};
  allFields.forEach(field => { newTouched[field] = true; });
  setTouched(newTouched);
  
  // Validate all fields before submission
  if (!validateAllFields()) {
    setImportError("Please fix the validation errors before submitting.");
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  
  setIsLoading(true);
  setImportError(null);
  
  const formDataWithFiles = new FormData();
  try {
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formDataWithFiles.append(key, value);
      }
    });

    // Add currency to the form data
    formDataWithFiles.append("currency", currency);  // This is the key line

    if (selectedFile) {
      formDataWithFiles.append("file", selectedFile);
    }

    const responseData = await ExecutePostWithParams("investors", formDataWithFiles, true);
    console.log(responseData);
    if (responseData) {
      setImportSuccess(responseData.message || "Investor registered successfully!");
      setFormData(initialData);
      setFormErrors({});
      setTouched({});
      setAttachedFiles([]);
      setSelectedFile(null);
      setCurrency("USD");
      setIsOtherCountry(false);
      setOtherCountryValue("");
      setTimeout(() => setImportSuccess(null), 5000);
    } else {
      setImportError("Failed to save data");
    }
  } catch (error) {
    setImportError(error.response?.data?.message || "An error occurred during registration. Please try again.");
    console.log("Failed to save data", error);
  } finally {
    setIsLoading(false);
  }
};
//** */
const showError = (fieldName) => {
    return touched[fieldName] && formErrors[fieldName];
  };

  return (
    <>
      <FormContainer title="Register Investor Information" onSubmit={handleSubmit}>
        {(isLoading || importError || importSuccess) && (
          <Grid item xs={12} md={12} alignContent={"center"}>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {importError && (
              <Alert severity="error" onClose={() => setImportError(null)} sx={{ mb: 2 }}>
                {importError}
              </Alert>
            )}
            {importSuccess && (
              <Alert severity="success" onClose={() => setImportSuccess(null)} sx={{ mb: 2 }}>
                {importSuccess}
              </Alert>
            )}
          </Grid>
        )}
        
        {/* Regular fields */}
        {Object.keys(initialData).map(key => {
          // Skip rendering nationality_origin, capital_in_usd, and description here
          if (key === "nationality_origin" || key === "capital_in_usd" || key === "description") {
            return null;
          }
          
          // Skip website - not required
          if (key === "website") {
            return null;
          }
          
          const hasError = showError(key);
          const isEmailField = key === "email";
          const isPhoneField = key === "phone_no";
          const isMobileField = key === "mobile_no";
          const isTINField = key === "tin_no";
          const isPositionField = key === "contact_person_position";
          
          // Determine if field is required
          let isRequired = true;
          if (isTINField || isPositionField) {
            isRequired = false;
          }
          
          // Set input props based on field type
          let inputProps = {};
          let inputType = "text";
          
          if (key === "tin_no") {
            inputProps = { minLength: 10, maxLength: 13 };
          }
          
          if (isEmailField) {
            inputType = "email";
            inputProps = { ...inputProps, autoComplete: "email" };
          }
          
          if (isPhoneField || isMobileField) {
            inputType = "tel";
            inputProps = { ...inputProps, autoComplete: "tel" };
          }
          
          return (
            <Grid item xs={12} md={6} key={key}>
              <TextField
                id={key}
                name={key}
                label={key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                variant="outlined"
                fullWidth={true}
                required={isRequired}
                value={formData[key] || ""}
                type={inputType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={hasError}
                helperText={hasError ? formErrors[key] : (isEmailField ? "example@domain.com" : (isPhoneField || isMobileField ? "e.g.,+251-000-000-000 / 0912345678" : (isTINField ? "Optional - 10-13 characters" : "")))}
                inputProps={inputProps}
              />
            </Grid>
          );
        })}
        
        {/* Nationality Origin, Currency, and Capital row */}
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            {!isOtherCountry ? (
              <TextField
                select
                id="nationality_origin"
                name="nationality_origin"
                label="Nationality Origin"
                variant="outlined"
                fullWidth={true}
                required={true}
                value={formData.nationality_origin || ""}
                onChange={handleCountryChange}
                onBlur={handleBlur}
                error={showError("nationality_origin")}
                helperText={showError("nationality_origin") ? formErrors.nationality_origin : "Select country of origin"}
                SelectProps={{
                  MenuProps: {
                    style: { maxHeight: 400 }
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select a country</em>
                </MenuItem>
                {countries.map(country => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
                <MenuItem value="other" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  + Other (Not in list)
                </MenuItem>
              </TextField>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  id="other_country"
                  name="other_country"
                  label="Enter Country Name"
                  variant="outlined"
                  fullWidth={true}
                  required={true}
                  value={otherCountryValue}
                  onChange={handleOtherCountryChange}
                  onBlur={handleOtherCountryBlur}
                  error={showError("nationality_origin")}
                  helperText={showError("nationality_origin") ? formErrors.nationality_origin : ""}
                  autoFocus
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsOtherCountry(false);
                    setOtherCountryValue("");
                  }}
                  sx={{ minWidth: '100px' }}
                >
                  Back to List
                </Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Currency"
              value={currency}
              onChange={handleCurrencyChange}
              variant="outlined"
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ETB">ETB</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="capital_in_usd"
              name="capital_in_usd"
              label="Capital"
              variant="outlined"
              fullWidth={true}
              required={true}
              value={formData.capital_in_usd || ""}
              type="number"
              onChange={handleChange}
              onBlur={handleBlur}
              error={showError("capital_in_usd")}
              helperText={showError("capital_in_usd") ? formErrors.capital_in_usd : "Enter capital amount"}
              InputProps={{
                endAdornment: <InputAdornment position="end">{currency}</InputAdornment>
              }}
              inputProps={{ min: 0, step: 1000 }}
            />
          </Grid>
        </Grid>

        {/* Description field */}
        <Grid item xs={12} md={12}>
          <TextField
            id="description"
            name="description"
            label="Description"
            variant="outlined"
            fullWidth={true}
            required={false}
            multiline={true}
            rows={2}
            value={formData.description || ""}
            onChange={handleChange}
            placeholder="Additional information about the investor..."
          />
        </Grid>

        {/* File attachment field */}
        <Grid item xs={12} md={12}>
          <IPDCAttachFile1 onChange={handleAttachmentsChange} isMultiple={true} />
        </Grid>
      </FormContainer>
    </>
  );
}